import { useState } from 'react';
import { Link, Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import ModuleShell from '../../components/ModuleShell';
import Sparkline from '../../components/Sparkline';
import {
  ADMIN_CREATE_EXPERIMENT,
  ADMIN_EXPERIMENTS,
  ADMIN_SET_EXPERIMENT_STATUS,
  ADMIN_UPDATE_EXPERIMENT_DRAFT,
} from '../../graphql/adminQueries';
import { formatDateTime, formatNumber } from '../../utils/format';
import { useAuth } from '../../auth/AuthContext';
import ExperimentDraftForm, { draftFromExperiment } from './ExperimentDraftForm';
import {
  ADVISORY_COPY,
  MOUNTED_NOTE,
  MUTEX_COPY,
  NOT_READY_COPY,
  STATUS_OPTIONS,
  STATUS_SECTIONS,
  UNMOUNTED_NOTE,
  armProgress,
  decisionLabel,
  decisionMeaning,
  experimentPreviewUrl,
  filterExperiments,
  intervalLabel,
  liftLabel,
  mutationMessage,
  needsCall,
  pageMounted,
  publicSiteOrigin,
  rateLabel,
  srmLabel,
  summarizeExperiments,
  treatmentLift,
} from './experimentView';

export {
  ADVISORY_COPY,
  MUTEX_COPY,
  NOT_READY_COPY,
  decisionLabel,
  intervalLabel,
  liftLabel,
  rateLabel,
  srmLabel,
  summarizeExperiments,
};

function decisionClass(decision) {
  return `decision-pill decision-${String(decision || 'unknown').replace(/_/g, '-')}`;
}

function ExperimentsBreadcrumb({ current }) {
  return (
    <nav className="experiment-breadcrumb" aria-label="Breadcrumb">
      <Link to="/experiments">Experiments</Link>
      <span aria-hidden="true"> / </span>
      <span>{current}</span>
    </nav>
  );
}

function HowToRead() {
  return (
    <details className="experiment-advisory">
      <summary>How to read this</summary>
      <p>{ADVISORY_COPY}</p>
    </details>
  );
}

function SampleMeter({ arm, planned }) {
  const percent = Math.round(arm.ratio * 100);
  return (
    <div className="experiment-meter">
      <div className="experiment-meter-label">
        <span>{arm.label || arm.key}</span>
        <span>
          {formatNumber(arm.exposures)} / {formatNumber(planned)}
          {' · '}
          {formatNumber(arm.remaining)} left
        </span>
      </div>
      <div
        className="experiment-meter-track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={planned}
        aria-valuenow={arm.exposures}
        aria-label={`${arm.key} sample progress`}
      >
        <span style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

function ExperimentPreview({ experiment, variants }) {
  const rows = variants?.length ? variants : experiment?.variants || [];
  const [variantKey, setVariantKey] = useState(rows[0]?.variantKey || rows[0]?.key || 'control');
  const selected = rows.find((row) => (row.variantKey || row.key) === variantKey) || rows[0];
  const selectedKey = selected?.variantKey || selected?.key || 'control';
  const mounted = experiment?.key ? pageMounted(experiment) : false;
  const href = experiment?.key ? experimentPreviewUrl(experiment, selectedKey) : '';

  return (
    <section className="experiment-preview" aria-label="Variant preview">
      <header className="experiment-card-header">
        <div>
          <h2>Preview</h2>
          <p className="experiment-meta">
            Forces this variant in your view only. It does not change status or visitor assignment.
          </p>
        </div>
        <div className="experiment-preview-switch" role="group" aria-label="Preview variant">
          {rows.map((row) => {
            const key = row.variantKey || row.key;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={key === selectedKey}
                onClick={() => setVariantKey(key)}
              >
                {row.label || key}
              </button>
            );
          })}
        </div>
      </header>
      <p className="experiment-preview-specimen" data-variant={selectedKey}>
        {selected?.label || selectedKey}
      </p>
      {mounted && href ? (
        <>
          <p className="experiment-meta">{MOUNTED_NOTE} {href}</p>
          <iframe
            className="experiment-preview-frame"
            title={`Preview ${experiment.name} ${selectedKey}`}
            src={href}
            credentialless=""
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
          <p>
            <a href={href} target="_blank" rel="noreferrer">Open this variant on the public site</a>
          </p>
        </>
      ) : (
        <p className="experiment-meta">{UNMOUNTED_NOTE}</p>
      )}
    </section>
  );
}

function ExperimentCard({
  experiment,
  canEdit,
  onStatusChange,
  statusBusy,
  statusError,
  displayedStatus,
}) {
  const planned = experiment.plannedExposuresPerArm;
  const ready = Boolean(experiment.decisionReady);
  const progress = armProgress(experiment);
  const lift = treatmentLift(experiment);
  const mounted = pageMounted(experiment);
  const series = experiment.exposureSeries || [];
  const history = experiment.statusHistory || [];

  return (
    <article className="chart-card experiment-card" aria-labelledby={`exp-${experiment.key}`}>
      <header className="experiment-card-header">
        <div>
          <h2 id={`exp-${experiment.key}`}>{experiment.name}</h2>
          <p className="experiment-meta">
            <code>{experiment.key}</code>
            {' · '}
            {experiment.surface}
            {' · '}
            {experiment.pagePath}
          </p>
        </div>
        <div className="experiment-status">
          <span className={decisionClass(experiment.decision)}>
            {decisionLabel(experiment.decision)}
          </span>
          <span className={`status-pill status-${displayedStatus}`}>{displayedStatus}</span>
          {canEdit ? (
            <label className="experiment-status-select">
              <span>Status</span>
              <select
                value={displayedStatus}
                disabled={statusBusy}
                onChange={(event) => onStatusChange(experiment.key, event.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <p className="experiment-meta">Staff can preview. An admin changes status.</p>
          )}
        </div>
      </header>

      <p className="experiment-decision-meaning">{decisionMeaning(experiment.decision)}</p>
      {statusError ? <p className="module-status module-status-error" role="alert">{statusError}</p> : null}
      <p className={`experiment-srm${experiment.srmFlagged ? ' experiment-srm-flagged' : ''}`}>
        {srmLabel(experiment)}
      </p>
      {lift ? (
        <p className="experiment-lift">Lift vs control: {liftLabel(lift.lift, lift.liftLow, lift.liftHigh)}</p>
      ) : null}
      {!ready ? <p className="experiment-not-ready">{NOT_READY_COPY}</p> : null}
      {experiment.mutexConflict ? (
        <p className="experiment-mutex-warning" role="alert">{MUTEX_COPY}</p>
      ) : null}
      <p className="experiment-meta">{mounted ? MOUNTED_NOTE : UNMOUNTED_NOTE}</p>
      {experiment.updatedAt ? (
        <p className="experiment-meta">Last status update: {formatDateTime(experiment.updatedAt)}</p>
      ) : (
        <p className="experiment-meta">No status override yet. Catalog default stays draft until an admin saves one.</p>
      )}
      {experiment.statusSource ? (
        <p className="experiment-meta">Status source: {experiment.statusSource}</p>
      ) : null}

      <div className="experiment-meters">
        {progress.arms.map((arm) => (
          <SampleMeter key={arm.key} arm={arm} planned={planned} />
        ))}
      </div>

      <ExperimentPreview experiment={experiment} />

      <table className="data-table experiment-variants-table">
        <caption className="range-label">Variants (lifetime)</caption>
        <thead>
          <tr>
            <th scope="col">Variant</th>
            <th scope="col">Exposures</th>
            <th scope="col">Conversions</th>
            <th scope="col">Rate</th>
            <th scope="col">Wilson 95%</th>
            <th scope="col">Lift vs control</th>
          </tr>
        </thead>
        <tbody>
          {(experiment.variants || []).map((variant) => (
            <tr key={variant.variantKey}>
              <td>
                <strong>{variant.variantKey}</strong>
                <div className="experiment-variant-label">{variant.label}</div>
              </td>
              <td>
                {formatNumber(variant.exposures)} / {formatNumber(planned)}
              </td>
              <td>{formatNumber(variant.conversions)}</td>
              <td>{rateLabel(variant.conversionRate)}</td>
              <td>{intervalLabel(variant.rateLow, variant.rateHigh)}</td>
              <td>
                {variant.lift == null ? '—' : liftLabel(variant.lift, variant.liftLow, variant.liftHigh)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {(experiment.guardrails || []).map((guardrail) => (
        <div key={guardrail.goal} className="experiment-guardrail">
          <h3 className="experiment-guardrail-title">
            Guardrail: {guardrail.label} (`{guardrail.goal}`)
            {guardrail.failing ? ' — failing' : ' — holding'}
          </h3>
          <table className="data-table experiment-guardrail-table">
            <caption className="range-label">Guardrail rates</caption>
            <thead>
              <tr>
                <th scope="col">Variant</th>
                <th scope="col">Events</th>
                <th scope="col">Rate</th>
              </tr>
            </thead>
            <tbody>
              {(guardrail.variants || []).map((row) => (
                <tr key={row.variantKey}>
                  <td>{row.variantKey}</td>
                  <td>{formatNumber(row.conversions)}</td>
                  <td>{rateLabel(row.rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <section aria-label="Exposure timeline">
        <h3 className="experiment-guardrail-title">Exposure timeline</h3>
        {series.length >= 2 ? (
          <Sparkline
            series={series}
            field="exposures"
            dateField="date"
            viewBox="0 0 240 48"
            formatValue={(value) => formatNumber(value)}
          />
        ) : (
          <p className="experiment-meta">
            {series.length === 1
              ? `${series[0].date}: ${formatNumber(series[0].exposures)} exposures`
              : 'No dated exposure rows yet.'}
          </p>
        )}
      </section>

      <section aria-label="Status history">
        <h3 className="experiment-guardrail-title">Status history</h3>
        {history.length ? (
          <ol className="experiment-history">
            {history.map((row) => (
              <li key={`${row.at}-${row.to}`}>
                {formatDateTime(row.at)}: {row.from || 'unset'} → {row.to}
              </li>
            ))}
          </ol>
        ) : (
          <p className="experiment-meta">No status changes recorded.</p>
        )}
      </section>
    </article>
  );
}

function ExperimentRow({ experiment }) {
  const href = `/experiments/${encodeURIComponent(experiment.key)}`;
  const progress = armProgress(experiment);
  const lift = treatmentLift(experiment);
  const label = `${experiment.name}, ${experiment.status}, ${decisionLabel(experiment.decision)}`;
  return (
    <li>
      <Link className="experiment-row" to={href} aria-label={label}>
        <span className="experiment-row-main">
          <span className="experiment-row-name">{experiment.name}</span>
          <span className="experiment-meta">
            <code>{experiment.key}</code>
            {' · '}
            {experiment.pagePath}
            {pageMounted(experiment) ? '' : ' · not mounted'}
          </span>
          <SampleMeter arm={progress.slowest} planned={progress.planned} />
        </span>
        <span className="experiment-row-metrics">
          <span>{formatNumber(experiment.exposures)} exposures</span>
          <span>{lift ? liftLabel(lift.lift, lift.liftLow, lift.liftHigh) : '—'}</span>
        </span>
        <span className="experiment-row-pills">
          {needsCall(experiment) ? <span className="decision-pill decision-srm">Needs a call</span> : null}
          <span className={decisionClass(experiment.decision)}>{decisionLabel(experiment.decision)}</span>
          <span className={`status-pill status-${experiment.status}`}>{experiment.status}</span>
        </span>
      </Link>
    </li>
  );
}

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'call', label: 'Needs a call' },
  { id: 'running', label: 'Running' },
  { id: 'paused', label: 'Paused' },
  { id: 'draft', label: 'Draft' },
  { id: 'completed', label: 'Completed' },
];

export function ExperimentsIndex() {
  const { experiments, loading, error, canEdit } = useOutletContext();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const summary = summarizeExperiments(experiments);
  const visible = filterExperiments(experiments, { query, filter });
  const grouped = (filter === 'all'
    ? STATUS_SECTIONS.map((section) => ({
      ...section,
      items: visible.filter((experiment) => experiment.status === section.value),
    }))
    : [{
      value: filter,
      label: FILTERS.find((item) => item.id === filter)?.label || 'Matches',
      items: visible,
    }]
  ).filter((section) => section.items.length > 0);

  return (
    <ModuleShell
      title="Experiments"
      description="Lifetime decisions across every registered experiment. Counts are consented first-party analytics, not unique visitors."
      loading={loading && experiments.length === 0}
      loadingLabel="Loading experiments…"
      error={error}
      errorLabel="Experiments query unavailable. Deploy website-api adminExperiments."
      actions={canEdit ? <Link className="experiment-create-link" to="/experiments/new">New draft</Link> : null}
    >
      <HowToRead />
      {summary.mutexConflict ? (
        <p className="experiment-mutex-warning" role="alert">{MUTEX_COPY}</p>
      ) : null}
      {!error ? (
        <div className="kpi-grid experiment-overview-kpis" aria-label="Overall experiment stats">
          <article className="kpi-card">
            <p className="kpi-label">Needs a call</p>
            <p className="kpi-value">{formatNumber(summary.needsCall)}</p>
          </article>
          <article className="kpi-card">
            <p className="kpi-label">Ship candidates</p>
            <p className="kpi-value">{formatNumber(summary.shipCandidates)}</p>
          </article>
          <article className="kpi-card">
            <p className="kpi-label">Lifetime exposures</p>
            <p className="kpi-value">{formatNumber(summary.exposures)}</p>
          </article>
          <article className="kpi-card">
            <p className="kpi-label">Lifetime conversions</p>
            <p className="kpi-value">{formatNumber(summary.conversions)}</p>
          </article>
          <article className="kpi-card">
            <p className="kpi-label">Mutex</p>
            <p className="kpi-value">{summary.mutexConflict ? 'Conflict' : 'Clear'}</p>
          </article>
        </div>
      ) : null}

      <div className="experiment-toolbar">
        <label className="experiment-field">
          <span>Search</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Name, key, page, decision"
          />
        </label>
        <div className="experiment-filters" role="group" aria-label="Filter experiments">
          {FILTERS.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={filter === item.id}
              onClick={() => setFilter(item.id)}
            >
              {item.label}
              {item.id !== 'all' && item.id !== 'call' ? ` (${summary.counts[item.id] || 0})` : ''}
              {item.id === 'call' ? ` (${summary.needsCall})` : ''}
            </button>
          ))}
        </div>
      </div>

      {grouped.map((section) => (
        <section key={section.value} className="experiment-section" aria-label={`${section.label} experiments`}>
          <h2 className="experiment-section-title">{section.label} ({section.items.length})</h2>
          <ul className="experiment-row-list">
            {section.items.map((experiment) => (
              <ExperimentRow key={experiment.key} experiment={experiment} />
            ))}
          </ul>
        </section>
      ))}

      {!loading && !error && experiments.length === 0 ? (
        <p className="module-status">No experiments registered.</p>
      ) : null}
      {!loading && !error && experiments.length > 0 && visible.length === 0 ? (
        <p className="module-status">No experiments match this filter.</p>
      ) : null}
    </ModuleShell>
  );
}

export function ExperimentDetail() {
  const {
    experiments,
    loading,
    error,
    canEdit,
    onStatusChange,
    statusBusy,
    statusError,
    pendingStatus,
    onSaveDraft,
    draftBusy,
    draftError,
  } = useOutletContext();
  const { key: rawKey } = useParams();
  const key = rawKey ? decodeURIComponent(rawKey) : '';
  const experiment = experiments.find((item) => item.key === key);
  const missing = !loading && !error && experiments.length > 0 && !experiment;
  const emptyCatalog = !loading && !error && experiments.length === 0;
  const displayedStatus = pendingStatus && experiment && pendingStatus.key === experiment.key
    ? pendingStatus.status
    : experiment?.status;

  return (
    <ModuleShell
      title={experiment?.name || 'Experiment'}
      description={
        experiment
          ? 'Lifetime decision view. Counts are consented first-party analytics, not unique visitors.'
          : 'Open an experiment from the list to see variants, intervals, and status controls.'
      }
      leading={<ExperimentsBreadcrumb current={experiment?.name || key || 'Detail'} />}
      loading={loading && !experiment}
      loadingLabel="Loading experiments…"
      error={error}
      errorLabel="Experiments query unavailable. Deploy website-api adminExperiments."
    >
      {experiment ? (
        <>
          <HowToRead />
          <ExperimentCard
            experiment={experiment}
            canEdit={canEdit}
            onStatusChange={onStatusChange}
            statusBusy={statusBusy}
            statusError={statusError}
            displayedStatus={displayedStatus}
          />
          {canEdit && experiment.status === 'draft' ? (
            <ExperimentDraftForm
              mode="edit"
              initialDraft={draftFromExperiment(experiment)}
              structureLocked={pageMounted(experiment)}
              busy={draftBusy}
              error={draftError}
              onSubmit={({ draft }) => onSaveDraft(experiment.key, draft)}
            />
          ) : null}
        </>
      ) : null}
      {missing || emptyCatalog ? (
        <p className="module-status" role="status">
          Experiment `{key}` was not found.{' '}
          <Link to="/experiments">Back to experiments</Link>
        </p>
      ) : null}
    </ModuleShell>
  );
}

export function ExperimentCreate() {
  const { canEdit, onCreateDraft, draftBusy, draftError } = useOutletContext();
  const navigate = useNavigate();
  return (
    <ModuleShell
      title="New experiment"
      description="Creates a draft. The public site does not assign a new key until a page mounts it."
      leading={<ExperimentsBreadcrumb current="New draft" />}
    >
      {canEdit ? (
        <>
          <ExperimentDraftForm
            mode="create"
            busy={draftBusy}
            error={draftError}
            onSubmit={({ key, draft }) => onCreateDraft(key, draft, navigate)}
          />
        </>
      ) : (
        <p className="module-status">Staff can preview existing experiments. An admin creates drafts.</p>
      )}
    </ModuleShell>
  );
}

export default function ExperimentsModule() {
  const { isStaff } = useAuth();
  const { data, loading, error, refetch } = useQuery(ADMIN_EXPERIMENTS, {
    fetchPolicy: 'cache-and-network',
  });
  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [draftError, setDraftError] = useState('');
  const [setStatus, { loading: statusBusy }] = useMutation(ADMIN_SET_EXPERIMENT_STATUS, {
    onError: (err) => {
      setPendingStatus(null);
      setStatusError(mutationMessage(err));
    },
    onCompleted: async (payload) => {
      const next = payload?.adminSetExperimentStatus;
      try {
        const result = await refetch();
        const row = (result.data?.adminExperiments || []).find((item) => item.key === next?.key);
        if (!row || row.status !== next?.status) {
          setStatusError(`Status is still ${row?.status || 'unchanged'} on the server.`);
        } else {
          setStatusError('');
        }
      } catch (err) {
        setStatusError(mutationMessage(err));
      } finally {
        setPendingStatus(null);
      }
    },
  });
  const [createDraft, { loading: createBusy }] = useMutation(ADMIN_CREATE_EXPERIMENT);
  const [updateDraft, { loading: updateBusy }] = useMutation(ADMIN_UPDATE_EXPERIMENT_DRAFT);

  const experiments = data?.adminExperiments || [];

  const handleStatusChange = (key, status) => {
    setStatusError('');
    setPendingStatus({ key, status });
    setStatus({ variables: { key, status } });
  };

  const handleCreateDraft = async (key, draft, navigate) => {
    setDraftError('');
    try {
      await createDraft({ variables: { key, draft } });
      await refetch();
      navigate(`/experiments/${encodeURIComponent(key)}`);
    } catch (err) {
      setDraftError(mutationMessage(err));
    }
  };

  const handleSaveDraft = async (key, draft) => {
    setDraftError('');
    try {
      await updateDraft({ variables: { key, draft } });
      await refetch();
    } catch (err) {
      setDraftError(mutationMessage(err));
    }
  };

  return (
    <Outlet
      context={{
        experiments,
        loading,
        error,
        canEdit: !isStaff,
        onStatusChange: handleStatusChange,
        statusBusy,
        statusError,
        pendingStatus,
        onCreateDraft: handleCreateDraft,
        onSaveDraft: handleSaveDraft,
        draftBusy: createBusy || updateBusy,
        draftError,
        publicOrigin: publicSiteOrigin(),
      }}
    />
  );
}
